import React, { useEffect } from 'react';
import { Radio } from '@spark';
import {
    emptyResponseDefault,
    emptyResponseOptions,
    emptyResponseSectionCopy,
} from '@/features/datasets/utils/emptyResponsesHandling';
import { HelpIcon } from '../shared/TaskTypeControls';

/**
 * Data Collection empty-response choice (labels / entities / summaries /
 * outputs). Spark radios matching wizard FieldRow patterns.
 *
 * Shown only when a response column is selected (parent gates visibility).
 * Multilabel classification gets None Selected / Unlabeled / Generate with AI /
 * Exclude; every other task type gets Unlabeled / Generate with AI / Exclude.
 *
 * Radios are controlled individually (not via RadioGroup) so each option can
 * keep a description line under the label — RadioGroup only enhances direct
 * Radio children.
 */
const EmptyResponsesSection = ({
    value,
    onChange,
    isMultilabel = false,
    taskType,
    titleSuffix = '',
}) => {
    const options = emptyResponseOptions({ isMultilabel, taskType });
    const allowedValues = options.map((opt) => opt.value);
    const defaultValue = emptyResponseDefault(isMultilabel);
    const selectedValue = allowedValues.includes(value) ? value : defaultValue;
    const copy = emptyResponseSectionCopy(taskType);
    const suffix = titleSuffix ? ` ${titleSuffix}` : '';
    const groupName = `empty-responses${titleSuffix || ''}`;

    useEffect(() => {
        if (!allowedValues.includes(value)) {
            onChange(defaultValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, isMultilabel, taskType]);

    return (
        <div className="cw-dc-field-row">
            <div className="cw-dc-field-row__label">
                <h3 className="cw-dc-section-title cw-dc-section-title--with-help">
                    <span>
                        {copy.title}
                        {suffix}
                    </span>
                    <HelpIcon text={copy.tooltip} />
                </h3>
                <p className="cw-dc-field-row__desc">{copy.description}</p>
            </div>
            <div className="cw-dc-field-row__controls">
                <div className="cw-dc-empty-responses" role="radiogroup" aria-label={copy.title}>
                    {options.map((opt) => (
                        <div key={opt.value} className="cw-dc-empty-responses__option">
                            <Radio
                                name={groupName}
                                value={opt.value}
                                label={opt.title}
                                status={selectedValue === opt.value ? 'active' : 'inactive'}
                                onChange={() => onChange(opt.value)}
                            />
                            <p className="cw-dc-empty-responses__option-desc">{opt.description}</p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default EmptyResponsesSection;
